import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Card, CardContent, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { colors } from "../../theme/muiColors";
import { updateBasicCalculator } from "../../redux/features/basicCalculator";
import { calculateBasicCalculator, menuPropsModal } from "../../utils";

const typeOfServices = [
  { id: 1, name: "Deep Cleaning" },
  { id: 2, name: "Office Cleaning" },
  { id: 3, name: "Move On Cleaning" },
  { id: 4, name: "Other" },
];

const sxButton = {
  bgcolor: colors.blue[500],
  borderRadius: 4,
  minWidth: 100,
};

export const BasicCalculatorForm = ({ setLoading, sx }) => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.basicCalculatorState);
  const [serviceType, setServiceType] = useState("");

  const handleSelectedChange = (value) => setServiceType(value);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateBasicCalculator({ [name]: value }));
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        typeOfServiceId: serviceType || 1,
        billingHourlyRate: Number(state.billingHourlyRate),
        employeePayRate: Number(state.employeePayRate),
        payrollTax: Number(state.payrollTax),
        workersComp: Number(state.workersComp),
        benefits: Number(state.benefits),
        otherCosts: Number(state.otherCosts),
      };
      const res = await calculateBasicCalculator(data);
      dispatch(updateBasicCalculator({ state: 1, ...res.data }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        ...sx,
        width: "95%",
        mx: "auto",
        pt: 4,
        pb: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography variant="subtitle1" textAlign="center">
        Basic Calculator
      </Typography>

      <form
        onSubmit={handleCalculate}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Card elevation={0}>
          <CardContent>
            <Typography variant="subtitle1" color="textSecondary" mb={2}>
              Service Configuration
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Type of Service</InputLabel>
              <Select
                MenuProps={menuPropsModal}
                value={serviceType}
                label="Type of Service"
                onChange={(e) => handleSelectedChange(e.target.value)}
                required
              >
                {typeOfServices.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        <Card elevation={0}>
          <CardContent>
            <Typography variant="subtitle1" color="textSecondary" mb={2}>
              Billing Information
            </Typography>
            <Grid container spacing={2}>
              {[
                {
                  name: "billingHourlyRate",
                  label: "Billing Hourly Rate",
                  adorn: "$",
                },
                {
                  name: "employeePayRate",
                  label: "Employee Pay Rate",
                  adorn: "$",
                },
              ].map((f) => (
                <Grid item xs={12} md={6} key={f.name}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    name={f.name}
                    label={f.label}
                    value={state[f.name]}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{f.adorn}</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0}>
          <CardContent>
            <Typography variant="subtitle1" color="textSecondary" mb={2}>
              Operation Expenses
            </Typography>
            <Grid container spacing={2}>
              {[
                { name: "payrollTax", label: "Payroll Tax", adorn: "%" },
                { name: "workersComp", label: "Workers Comp.", adorn: "%" },
                { name: "benefits", label: "Benefits", adorn: "%" },
                { name: "otherCosts", label: "Other Costs", adorn: "%" },
              ].map((f) => (
                <Grid item xs={12} md={6} key={f.name}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    name={f.name}
                    label={f.label}
                    value={state[f.name]}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{f.adorn}</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="center">
          <Button type="submit" variant="contained" size="large" sx={{ ...sxButton }}>
            Calculate
          </Button>
        </Box>
      </form>
    </Box>
  );
};
